import { snapshotCommitFiles } from "../stepTrees";
import { refreshConnectorUsages } from "../refreshConnectorUsages";
import * as z from "zod";
import { userFacingError } from "../authorization";
import { copyAllCommitFiles } from "../commitFiles";
import { connectorSuppliersForReactivation } from "../connectorUsagePolicies";
import { zql } from "../generatedDrizzleSchema";
import { inheritBranchContributors } from "../inheritBranchContributors";
import { assertSuppliersAvailableToDependent } from "../rbac/crossResourceAvailability";
import { creatorColumns } from "../rbac/roles";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema } from "../id";

export const restoreToCommit = mutator({
  schema: z.object({
    branchId: z.string(),
    targetCommitId: z.string(),
    newCommitId: clientGeneratableIdSchema,
    chatId: clientGeneratableIdSchema,
    entryId: clientGeneratableIdSchema,
    restoredFromMessage: z.string().nullable(),
  }),
  resolve: {
    branch: { kind: "branch", from: "branchId" },
    targetCommit: { kind: "commit", from: "targetCommitId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.branch.workflow.spaceId,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      newCommitId,
      chatId: newChatId,
      entryId,
      restoredFromMessage,
      branch,
      targetCommit,
    },
  }) => {
    const creator = creatorColumns(ctx);
    const now = Date.now();

    if (targetCommit.branch.workflowId !== branch.workflowId) {
      throw userFacingError("Commit not found");
    }

    if (!branch.headCommitId) {
      throw userFacingError("Branch has no head commit");
    }

    // Rewinding live in place would bypass push-to-live’s gates.
    if (branch.name === "main") {
      throw userFacingError(
        "Main can’t be restored in place. Restore to a new branch and push it live."
      );
    }

    const suppliers = await connectorSuppliersForReactivation(tx, {
      workflowId: branch.workflowId,
      commitIds: [targetCommit.id],
    });
    await assertSuppliersAvailableToDependent(
      tx,
      ctx.tenantId,
      "space_uses_connected_apps",
      branch.workflow.spaceId,
      suppliers,
      "restore workflow"
    );

    // Check if the current head commit has a message (is already a checkpoint)
    const [currentHead] = await tx.run(
      zql.workflowCommits.where("id", branch.headCommitId)
    );

    // If not already a checkpoint, save current state so user can undo the restore
    if (currentHead && !currentHead.message) {
      await snapshotCommitFiles({
        tx,
        commitId: branch.headCommitId!,
        tenantId: ctx.tenantId,
      });
      await tx.mutate.workflowCommits.update({
        id: branch.headCommitId,
        message: "Before restore",
      });
    }

    // Create new checkpoint as a clone of the target
    await tx.mutate.workflowCommits.insert({
      id: newCommitId,
      workflowId: branch.workflowId,
      branchId: branch.id,
      parentCommitId: branch.headCommitId,
      chatId: newChatId,
      message: restoredFromMessage,
      createdAt: now,
      ...creator,
      tenantId: ctx.tenantId,
    });

    // Copy all files from target commit to new commit
    await copyAllCommitFiles(
      { tx, commitId: targetCommit.id, tenantId: ctx.tenantId },
      newCommitId
    );

    await inheritBranchContributors(
      tx,
      ctx,
      { ...targetCommit.branch, workflow: branch.workflow },
      branch.id
    );

    // Update branch head
    await tx.mutate.workflowBranches.update({
      id: branch.id,
      headCommitId: newCommitId,
    });
    await refreshConnectorUsages(tx, newCommitId);

    // Create a new chat on the new commit with a restore message
    await tx.mutate.chats.insert({
      id: newChatId,
      commitId: newCommitId,
      createdAt: now,
      lastChatEntryType: "SystemMessage",
      tenantId: ctx.tenantId,
    });

    const text = restoredFromMessage
      ? `Restored to: “${restoredFromMessage}”`
      : "Restored to previous version";

    await tx.mutate.chatEntries.insert({
      id: entryId,
      chatId: newChatId,
      entryType: "SystemMessage",
      createdAt: now,
      previousEntryId: null,
      tenantId: ctx.tenantId,
    });
    await tx.mutate.systemMessages.insert({
      entryId,
      text,
      tenantId: ctx.tenantId,
    });

    await tx.mutate.workflowBranches.update({
      id: branch.id,
      lastChatEntryAt: now,
    });

    await tx.mutate.workflows.update({
      id: branch.workflowId,
      updatedAt: now,
    });
  },
});
